import { useState } from 'react'
import { createPortal } from 'react-dom'
import { getUserShareLinks } from '../../services/api'
import { getCurrentUserUuid } from '../../services/userManager'

interface ShareLink {
  shareId: string
  projectUuid: string
  projectName?: string
  createdAt: string
  expiresAt: string | null
  allowDownload?: boolean
  message?: string | null
  hasPassword?: boolean
  lockInterface?: boolean
  shareUrl: string
}

interface LinkDialogProps {
  initialUrl?: string
  onSave: (url: string) => void
  onClose: () => void
}

export const LinkDialog = ({ initialUrl = '', onSave, onClose }: LinkDialogProps) => {
  const [url, setUrl] = useState(initialUrl)
  const [error, setError] = useState('')
  const [showShareLinks, setShowShareLinks] = useState(false)
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([])
  const [isLoadingLinks, setIsLoadingLinks] = useState(false)
  const [linksError, setLinksError] = useState<string | null>(null)

  const handleSave = () => {
    // 允许保存空链接（清除链接）
    onSave(url)
    onClose()
  }

  const handleClearLink = () => {
    onSave('')
    onClose()
  }

  const loadShareLinks = async () => {
    const userUuid = await getCurrentUserUuid()
    if (!userUuid) {
      setLinksError('请先登录或创建用户')
      return
    }

    try {
      setIsLoadingLinks(true)
      setLinksError(null)
      const response = await getUserShareLinks(userUuid)
      if (response.success) {
        setShareLinks(response.data || [])
      } else {
        setLinksError(response.message || '加载分享链接失败')
      }
    } catch (err: any) {
      setLinksError(err.message || '加载分享链接失败')
    } finally {
      setIsLoadingLinks(false)
    }
  }

  const handleToggleShareLinks = () => {
    if (!showShareLinks) {
      loadShareLinks()
    }
    setShowShareLinks(!showShareLinks)
  }

  const handleSelectShareLink = (shareUrl: string) => {
    setUrl(shareUrl)
    setShowShareLinks(false)
    setError('')
  }

  // Use Portal to render on top of everything
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
            </svg>
            任意门 (添加链接)
          </h3>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              跳转链接 (URL)
            </label>
            <div className="relative">
              <input
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value)
                  setError('')
                }}
                placeholder="https://... 或 /share/..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              提示：可以输入外部网址，或者选择下方的画布分享链接。
            </p>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
          </div>

          {/* 清除链接按钮 */}
          {initialUrl && (
            <button
              onClick={handleClearLink}
              className="w-full mb-4 px-4 py-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
            >
              清除当前链接
            </button>
          )}

          {/* 选择画布分享链接 */}
          <div className="border-t border-gray-200 pt-4">
            <button
              onClick={handleToggleShareLinks}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="3" y1="9" x2="21" y2="9"></line>
                  <line x1="9" y1="21" x2="9" y2="9"></line>
                </svg>
                选择我的画布
              </span>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                className={`transition-transform ${showShareLinks ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>

            {showShareLinks && (
              <div className="mt-3 max-h-60 overflow-y-auto">
                {isLoadingLinks ? (
                  <div className="text-center py-4 text-gray-500">加载中...</div>
                ) : linksError ? (
                  <div className="text-center py-4 text-red-600 text-sm">{linksError}</div>
                ) : shareLinks.length === 0 ? (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    暂无分享链接，请先在分享管理中创建分享链接
                  </div>
                ) : (
                  <div className="space-y-2">
                    {shareLinks.map((link) => {
                      const isSelected = url === link.shareUrl
                      return (
                        <button
                          key={link.shareId}
                          onClick={() => handleSelectShareLink(link.shareUrl)}
                          className={`w-full text-left px-3 py-2 rounded-md border transition-colors ${
                            isSelected
                              ? 'bg-blue-50 border-blue-300 text-blue-900'
                              : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-900'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 pr-2">
                              <div className="text-sm font-medium truncate">
                                {link.projectName || link.shareUrl}
                              </div>
                              <div className="text-xs text-gray-500 truncate">
                                {link.shareUrl}
                              </div>
                            </div>
                            {isSelected && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-blue-600 flex-shrink-0">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm"
          >
            确定
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
